import React from 'react';
import { Link } from 'react-router-dom';
const ComputingIT = () => {
  const programDetails = {
    title: 'Computing & Information Technology',
    degree: 'Bachelor of Science (BSc Hons)',
    duration: '3-4 Years',
    credits: '180 ECTS',
    intake: 'January, May, September',
    
    overview: 'Our Computing & IT program is designed to equip students with cutting-edge technical skills and theoretical knowledge in the rapidly evolving field of information technology. The curriculum is industry-aligned and focuses on practical, hands-on learning to prepare graduates for successful careers in technology.',
    
    highlights: [
      'State-of-the-art computer labs and facilities',
      'Industry-certified faculty with real-world experience',
      'Opportunities for internships with leading tech companies',
      'Focus on emerging technologies (AI, Cloud Computing, Cybersecurity)',
      'Project-based learning approach'
    ],
    
    specializations: [
      'Software Engineering',
      'Cybersecurity',
      'Data Science',
      'Cloud Computing',
      'Artificial Intelligence',
      'Web & Mobile Development'
    ],
    
    careerPaths: [
      'Software Developer',
      'Cybersecurity Analyst',
      'Data Scientist',
      'Cloud Solutions Architect',
      'IT Project Manager',
      'Systems Analyst',
      'Mobile App Developer',
      'Web Developer'
    ],
    
    entryRequirements: [
      'High School Diploma with mathematics',
      'Foundation Program completion or equivalent',
      'English language proficiency (IELTS 6.0 or equivalent)',
      'Basic programming knowledge preferred',
      'Personal statement and interview'
    ],
    
    facilities: [
      'Dedicated computer labs with latest hardware',
      'Networking and cybersecurity lab',
      'Software development studio',
      '24/7 access to computing resources',
      'Collaboration spaces for group projects'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{programDetails.title}</h1>
          <p className="text-2xl text-blue-100">{programDetails.degree}</p>
          <div className="flex flex-wrap gap-6 text-blue-100 mt-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Duration: {programDetails.duration}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {programDetails.credits} ECTS
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Intake: {programDetails.intake}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Program Overview */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Program Overview</h2>
              <p className="text-gray-700 mb-6">{programDetails.overview}</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Program Highlights</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {programDetails.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">{highlight}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Specializations */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Specializations</h2>
              <p className="text-gray-700 mb-6">
                In the final year, students can choose to specialize in one of the following cutting-edge areas:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {programDetails.specializations.map((specialization, index) => (
                  <div key={index} className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                    <h3 className="font-semibold text-blue-800">{specialization}</h3>
                  </div>
                ))}
              </div>
            </section>

            {/* Career Paths */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Career Opportunities</h2>
              <p className="text-gray-700 mb-6">
                Our graduates are well-prepared for various roles in the technology sector, including:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {programDetails.careerPaths.map((path, index) => (
                  <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-gray-700">{path}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Facilities */}
            <section className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Facilities & Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {programDetails.facilities.map((facility, index) => (
                  <div key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{facility}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Entry Requirements */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Entry Requirements</h2>
              <ul className="space-y-3">
                {programDetails.entryRequirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/admissions/apply-now"
                className="mt-6 inline-block bg-blue-900 text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors w-full text-center"
              >
                Apply Now
              </Link>
            </div>

            {/* Quick Links */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Related Programs</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/academics/foundation" className="text-blue-700 hover:underline">
                    Foundation in Computing
                  </Link>
                </li>
                <li>
                  <Link to="/academics/business-management" className="text-blue-700 hover:underline">
                    Business Information Systems
                  </Link>
                </li>
                <li>
                  <Link to="/academics/cybersecurity" className="text-blue-700 hover:underline">
                    Cybersecurity (MSc)
                  </Link>
                </li>
                <li className="pt-2 mt-4 border-t border-blue-100">
                  <Link to="/admissions/scholarships" className="text-blue-700 font-medium hover:underline">
                    View Scholarship Opportunities →
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Have Questions?</h3>
              <p className="text-gray-700 mb-4">
                Our admissions team is here to help you with any questions about the program.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-md hover:bg-blue-200 transition-colors w-full text-center"
              >
                Contact Admissions
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Shape the Future with Technology</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our Computing & IT program and develop the skills to build innovative solutions for tomorrow's challenges.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/admissions/apply-now"
              className="bg-white text-blue-900 px-8 py-3 rounded-md font-medium hover:bg-blue-100 transition-colors"
            >
              Start Your Application
            </Link>
            <Link
              to="/virtual-tour"
              className="border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-blue-800 transition-colors"
            >
              Take a Virtual Tour
            </Link>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default ComputingIT;
